import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { RadioButton } from 'src/components/atoms';
import {
  DefaultOption,
  FormInput,
  handleSuccessToast,
  Modal,
} from 'src/components/molecules';
import {
  GroupType,
  useGetCoiDefaultGroupQuery,
  useGetDefaultGroupQuery,
  useSetCoiDefaultGroupMutation,
  useSetDefaultGroupMutation,
} from 'src/graphql';
import { useRoutingGroup } from 'src/hooks';
import { FormInputVariant } from 'src/types';
import { RoutingVariantType } from '../RoutingRuleAndPriority';
import {
  RoutingFallbackBody,
  RoutingFallbackHeader,
  RoutingFallbackInfo,
  RoutingFallbackModalParagraph,
  RoutingFallbackRadioContainer,
  RoutingFallbackStyled,
  RoutingFallbackSubtitle,
  RoutingFallbackText,
  RoutingFallbackTitle,
} from './RoutingFallback.styles';

interface Props {
  groups: GroupType[];
  isDragging?: boolean;
  marginTop: string;
  variant: RoutingVariantType;
}

export const RoutingFallback = ({
  groups,
  isDragging,
  marginTop,
  variant,
}: Props) => {
  const queryClient = useQueryClient();

  const { data: speakUpDefaultGroup } = useGetDefaultGroupQuery(undefined, {
    enabled: variant === 'speak-up',
  });
  const { data: coiDefaultGroup } = useGetCoiDefaultGroupQuery(undefined, {
    enabled: variant === 'coi',
  });

  const currentDefaultGroup =
    variant === 'coi'
      ? coiDefaultGroup?.getCoiDefaultGroup
      : speakUpDefaultGroup?.getDefaultGroup;

  const onSetDefaultGroupSuccess = (defaultGroupSet?: string | null) => {
    queryClient.refetchQueries({
      queryKey: [variant === 'coi' ? 'GetCoiDefaultGroup' : 'GetDefaultGroup'],
    });
    handleSuccessToast({
      toastMessage: 'Fallback rule enabled',
      toastDescription: defaultGroupSet
        ? 'All cases will be assigned to a Group'
        : 'All cases will be automatically assigned',
    });
  };

  const { mutate: setSpeakUpDefaultGroup } = useSetDefaultGroupMutation({
    onSuccess: ({ setDefaultGroup: defaultGroupSet }) =>
      onSetDefaultGroupSuccess(defaultGroupSet),
  });
  const { mutate: setCoiDefaultGroup } = useSetCoiDefaultGroupMutation({
    onSuccess: ({ setCoiDefaultGroup: defaultGroupSet }) =>
      onSetDefaultGroupSuccess(defaultGroupSet),
  });

  const setDefaultGroup =
    variant === 'coi' ? setCoiDefaultGroup : setSpeakUpDefaultGroup;

  const { groupDropdownState, selectedGroup } = useRoutingGroup({
    destinationGroup: currentDefaultGroup,
    disabled: currentDefaultGroup !== null,
    groups,
    variant,
  });

  const groupHelpText =
    variant === 'coi'
      ? 'Ensure the selected group has active Compliance Reviewers. Please choose a different group for the default group to work effectively.'
      : 'Ensure the selected group has active Case Managers. Please choose a different group for the default group to work effectively.';

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [automaticAssignment, setAutomaticAssignment] = useState(
    currentDefaultGroup === null,
  );

  useEffect(() => {
    setAutomaticAssignment(currentDefaultGroup === null);
  }, [currentDefaultGroup, groups]);

  return (
    <RoutingFallbackStyled isDragging={isDragging} marginTop={marginTop}>
      <RoutingFallbackHeader>
        <RoutingFallbackTitle>Fallback route</RoutingFallbackTitle>
        <RoutingFallbackInfo
          className="uil uil-info-circle"
          size="24px"
          onClick={() => setInfoModalOpen(true)}
        />
      </RoutingFallbackHeader>
      <RoutingFallbackBody>
        <div>
          <RoutingFallbackSubtitle>
            Automatically assign cases
          </RoutingFallbackSubtitle>
          <RoutingFallbackText>
            If there are no rules available, automatically assign the first
            available case manager as your fallback logic.
          </RoutingFallbackText>
          <RoutingFallbackRadioContainer>
            <RadioButton
              checked={automaticAssignment}
              id="routing-auto-assign-radio"
              onClick={() => {
                setDefaultGroup({ groupId: null });
                setAutomaticAssignment(true);
              }}
              label="Automatic assignment enabled"
              labelPosition="right"
              data-testid="routing-auto-assign-radio"
            />
          </RoutingFallbackRadioContainer>
        </div>
        <div>
          <RoutingFallbackSubtitle>
            Otherwise, fallback to a &apos;Group&apos;
          </RoutingFallbackSubtitle>
          <RoutingFallbackText>
            If you do not want to automatically assign, choose a
            &apos;Group&apos; for cases not matching the set rules.
          </RoutingFallbackText>
          <RoutingFallbackRadioContainer>
            <RadioButton
              checked={!automaticAssignment}
              id="routing-default-group-radio"
              onClick={() => setAutomaticAssignment(false)}
              label="Enable fallback 'Group'"
              labelPosition="right"
              data-testid="routing-default-group-radio"
            />
          </RoutingFallbackRadioContainer>
          {!automaticAssignment && (
            <FormInput
              data-testid="default-group-select"
              variant={FormInputVariant.DROPDOWN}
              key={selectedGroup?.id}
              options={(groups ?? []).map((group) => ({
                label: group.name ?? '',
                value: group.id,
              }))}
              value={
                selectedGroup
                  ? {
                      label: selectedGroup?.name ?? '',
                      value: selectedGroup?.id,
                    }
                  : null
              }
              placeholder="Example: Group 1"
              onChange={(option) =>
                option
                  ? setDefaultGroup({
                      groupId: (option as DefaultOption).value as string,
                    })
                  : null
              }
              state={groupDropdownState}
              helpText={groupHelpText}
              menuPlacement="top"
            />
          )}
        </div>
      </RoutingFallbackBody>
      <Modal
        size="medium"
        title="What is a fallback route?"
        description={
          <RoutingFallbackModalParagraph>
            Fallback is a backup plan that determines the sequence of actions
            for directing traffic. It comes into play when there&apos;s no other
            specific rule to follow. If you do not create any routing rules,
            cases will be automatically (randomly) assigned to the first
            available case manager in your organization. This will also happen
            if you have routing rules, but a case comes in that does not match
            the parameters set in those rules based on the hierarchy. Instead of
            random assignment, you can select one group where all cases that do
            not match the rules set in place will be routed to.
          </RoutingFallbackModalParagraph>
        }
        open={infoModalOpen}
        setOpen={setInfoModalOpen}
        onConfirm={() => setInfoModalOpen(false)}
        confirmButtonText="OK"
      />
    </RoutingFallbackStyled>
  );
};
